import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { checkConnection } from '@/services/airtable';

const LOGO_URL = "https://media-manager-c.questera.ai/greta-media/184c9c9a291724797b8ad828c55b10701c0f1ac03d6d548cee29a666ff97a2e9df5b13aa801d7dcc8d672f63188942d5/images/aW1hZ2UvcG5n/23a0726a4b7957f773bb9be8533262a8.png";

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === 'PurplePeopleEater_112') {
      onLogin();
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="bg-white p-8 rounded-2xl shadow-xl border border-slate-100 max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4 p-4">
            <img src={LOGO_URL} alt="Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Protected Dashboard</h1>
          <p className="text-slate-500 mt-2">Please enter the password to access your flowcharts.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              placeholder="Enter Password" 
              className={`w-full px-4 py-3 rounded-xl border ${error ? 'border-red-300 bg-red-50 text-red-900 placeholder-red-300' : 'border-slate-200 bg-slate-50 focus:border-indigo-500'} focus:outline-none transition-all`}
              autoFocus
            />
          </div>
          <button 
            type="submit" 
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-lg shadow-indigo-200 transition-all active:scale-95"
          >
            Unlock Dashboard
          </button>
        </form>
      </motion.div>
    </div>
  );
};

const Dashboard = ({ workspaces, onOpen, onCreate, onDelete, onRestore, onPermanentDelete, isAuthenticated, onLogin }) => {
  const [cloudStatus, setCloudStatus] = useState({ loading: true, success: false, message: 'Checking...' });
  const [activeTab, setActiveTab] = useState('published'); // 'published' | 'trash'

  useEffect(() => {
    verifyCloud();
  }, []);

  const verifyCloud = async () => {
    setCloudStatus({ loading: true, success: false, message: 'Checking...' });
    const status = await checkConnection();
    setCloudStatus({ loading: false, ...status });
  };

  if (!isAuthenticated) {
    return <LoginScreen onLogin={onLogin} />;
  }

  // Filter workspaces based on active tab
  // Default status is 'published' if undefined
  const filteredWorkspaces = workspaces.filter(w => {
    const status = w.status || 'published';
    return status === activeTab;
  });

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-slate-200 flex flex-col fixed h-full z-10">
        <div className="p-6">
          <h1 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" />
            Shift Creative
          </h1>
          <p className="text-xs text-slate-400 font-medium pl-11">Flowcharts</p>
        </div>
        
        <div className="px-4 space-y-1 flex-1">
          <button 
            onClick={() => setActiveTab('published')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === 'published' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}
          >
            <SafeIcon icon={FiIcons.FiGrid} className="text-lg" />
            Published
            {activeTab === 'published' && <span className="ml-auto bg-indigo-200 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full">{filteredWorkspaces.length}</span>}
          </button>
          
          <button 
            onClick={() => setActiveTab('trash')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === 'trash' ? 'bg-red-50 text-red-600' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}
          >
            <SafeIcon icon={FiIcons.FiTrash2} className="text-lg" />
            Trash
            {activeTab === 'trash' && <span className="ml-auto bg-red-200 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full">{filteredWorkspaces.length}</span>}
          </button>
        </div>

        <div className="p-4 border-t border-slate-100">
          {/* Cloud Status Indicator (Read Only) */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] uppercase font-bold tracking-wider border cursor-default ${cloudStatus.loading ? 'bg-slate-100 text-slate-400 border-slate-200' : cloudStatus.success ? 'bg-green-50 text-green-600 border-green-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
            <div className={`w-2 h-2 rounded-full ${cloudStatus.loading ? 'bg-slate-400' : cloudStatus.success ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="truncate">
              {cloudStatus.loading ? 'Connecting...' : cloudStatus.success ? 'Airtable Connected' : 'Connection Failed'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                {activeTab === 'published' ? 'Published Flowcharts' : 'Trash'}
              </h2>
              <p className="text-slate-500 mt-1">
                {activeTab === 'published' ? 'Manage your active sitemap projects.' : 'Items in trash can be restored or permanently deleted.'}
              </p>
            </div>
            {activeTab === 'published' && (
              <button 
                onClick={onCreate}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium shadow-lg shadow-indigo-200 transition-all active:scale-95 flex items-center gap-2"
              >
                <SafeIcon icon={FiIcons.FiPlus} /> New Project
              </button>
            )}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode='popLayout'>
              {filteredWorkspaces.map((workspace) => (
                <motion.div 
                  key={workspace.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  whileHover={{ y: -4 }}
                  className={`group bg-white rounded-2xl border shadow-sm hover:shadow-xl transition-all overflow-hidden relative ${activeTab === 'trash' ? 'border-red-100' : 'border-slate-200 hover:border-indigo-100'}`}
                >
                  <div className="p-6 pb-4 cursor-pointer" onClick={() => activeTab === 'published' && onOpen(workspace.id)}>
                    <div className="flex justify-between items-start mb-4">
                      <div className={`p-3 rounded-xl transition-colors ${activeTab === 'trash' ? 'bg-red-50 text-red-500' : 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white'}`}>
                        <SafeIcon icon={activeTab === 'trash' ? FiIcons.FiTrash : FiIcons.FiMap} className="text-2xl" />
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {activeTab === 'published' ? (
                          <button 
                            onClick={(e) => { e.stopPropagation(); if (confirm('Move this project to trash?')) onDelete(workspace.id); }}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Move to Trash"
                          >
                            <SafeIcon icon={FiIcons.FiTrash2} />
                          </button>
                        ) : (
                          <>
                             <button 
                              onClick={(e) => { e.stopPropagation(); onRestore(workspace.id); }}
                              className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Restore Project"
                            >
                              <SafeIcon icon={FiIcons.FiRotateCcw} />
                            </button>
                            <button 
                              onClick={(e) => { e.stopPropagation(); if (confirm('PERMANENTLY DELETE? This cannot be undone.')) onPermanentDelete(workspace.id); }}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Forever"
                            >
                              <SafeIcon icon={FiIcons.FiX} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-1 truncate">{workspace.title}</h3>
                    <p className="text-sm text-slate-400">
                      Modified: {new Date(workspace.lastModified).toLocaleDateString()}
                    </p>
                  </div>
                  
                  {activeTab === 'published' ? (
                    <div 
                      className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs font-medium text-slate-500 group-hover:bg-indigo-50/30 transition-colors cursor-pointer"
                      onClick={() => onOpen(workspace.id)}
                    >
                      <span className="flex items-center gap-1.5">
                        <SafeIcon icon={FiIcons.FiGrid} /> {workspace.menus?.length || 0} Sitemaps
                      </span>
                      <span className="flex items-center gap-1 text-indigo-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0">
                        Open Project <SafeIcon icon={FiIcons.FiArrowRight} />
                      </span>
                    </div>
                  ) : (
                    <div className="px-6 py-4 bg-red-50/50 border-t border-red-100 flex items-center justify-center text-xs font-bold text-red-500">
                      <SafeIcon icon={FiIcons.FiAlertTriangle} className="mr-2" /> In Trash
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
            
            {filteredWorkspaces.length === 0 && (
              <div className="col-span-full py-20 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl">
                <SafeIcon icon={activeTab === 'trash' ? FiIcons.FiTrash : FiIcons.FiInbox} className="text-4xl mx-auto mb-4 opacity-50" />
                <p>{activeTab === 'published' ? 'No published projects. Create one to get started!' : 'Trash is empty.'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;